import { FilterDateDashboard } from "../../../../components/Dashboard/ViewDatas/FilterDate";
import { ReactComponent as BagFood } from "../../../../assets/svg/icons/bagFood.svg";
import { DateModal } from "../../../../components/Modal/DateFilter";
import { useFilter } from "../../../../hooks/useFilter";
import Constants from "../../../../constants/Index";
import { useEffect, useMemo, useRef, useState } from "react";
import { MultiDropdownV2, OptionV2 } from "../../../../components/FormControlsV2";
import { Controller, useForm } from "react-hook-form";
import { format, set } from "date-fns";
import ChartBar from "../../../../components/Graph/Bar";
import ChartMultiLine from "../../../../components/Graph/MultiLine";
import { IOptionDoubleValuesProps, IOptionProps, IOptionsProps } from "../../../../@types/reactSelect/IOption";
import axios from "../../../../services/axios";
import { LeafletStaticMap } from "../../../../components/Leaflet/LeafletStaticMap/Index";
import { FlyToBounds, FlyToBoundsRefProps } from "../../../../components/Leaflet/FlyToBounds/Index";
import { Polygon } from "react-leaflet";
import { exportPastureManagementMicroAreaDataToExcel } from "../../../../utils/excel/exportPastureManagementMicroAreaData";
import {
    flattenSelection,
    primaryFarmIdFromSelection,
    selectionToNumericIds,
    selectionToSortedIdsKey,
    useFilterContextV2,
} from "../../../../contexts/FilterContextV2";

export default function DashboardManagementMicroArea() {
    const {
        rangeDays,
        setRangeDays,
        setHideMicroAreaFilter,
        setHideBatchFilter,
        setDashboard,
        setSideBarOptionSelected,
        setSelectRangeDate,
        snapshotMicroAreaMetrics,
        setSnapshotMicroAreaMetrics,
    } = useFilter();

    const { selectedFarms, selectedAreas } = useFilterContextV2();

    const farmsKey = useMemo(
        () => selectionToSortedIdsKey(selectedFarms),
        [selectedFarms],
    );
    const areasKey = useMemo(
        () => selectionToSortedIdsKey(selectedAreas),
        [selectedAreas],
    );

    const primaryFarmId = primaryFarmIdFromSelection(selectedFarms);
    const primaryFarmCoords = useMemo(() => {
        const first = flattenSelection(selectedFarms)[0];
        const info = first?.info as { map_coords?: unknown; coords?: unknown } | undefined;
        const c = info?.map_coords ?? info?.coords;
        return Array.isArray(c) && c.length > 0 ? c : undefined;
    }, [selectedFarms]);

    const {
        handleSubmit,
        watch,
        control,
        setValue
    } = useForm();

    const [optionsTypeManagement, setOptionsTypeManagement] = useState<IOptionsProps>([]);
    const [lotInstConsumo, setLotInstConsumo] = useState<IOptionDoubleValuesProps>();
    const [lotInstConsumoMicroArea, setLotInstConsumoMicroArea] = useState<IOptionsProps>([]);
    const [lotInstAltura, setLotInstAltura] = useState<IOptionDoubleValuesProps>();
    const [lotInstAlturaMicroArea, setLotInstAlturaMicroArea] = useState<IOptionsProps>([]);
    const [lotInstNota, setLotInstNota] = useState<IOptionDoubleValuesProps>();
    const [lotInstNotaMicroArea, setLotInstNotaMicroArea] = useState<IOptionsProps>([]);
    const [lotInstMovFaixa, setLotInstFaixa] = useState<IOptionDoubleValuesProps>();
    const [lotInstMovFaixaMicroArea, setLotInstFaixaMicroArea] = useState<IOptionsProps>([]);
    const [managementOptions, setManagementOptions] = useState<IOptionsProps>();
    const [lotInst, setLotInst] = useState<IOptionsProps>([]);
    const [areas, setAreas] = useState<IArea[]>();
    const [microAreas, setMicroAreas] = useState<IMicroArea[]>();
    const [microAreasSelected, setMicroAreasSelected] = useState<Number[]>();

    const typeManagement = watch("managementType")
    const groupManagement = watch("managementGroup")

    useEffect(() => {
        setHideMicroAreaFilter(true)
        setHideBatchFilter(true)
        setSideBarOptionSelected("DASHBOARD")
        setDashboard("MANAGEMENT_PASTURE")
        setSelectRangeDate(0)
        document.title = Constants.TITLE_DASHBOARD;

        axios
            .get("/micro-area-management/types")
            .then((response) => {
                const types: IOptionsProps = response.data.map((type: any) => ({
                    label: type.name,
                    value: String(type.id),
                    info: type.monitoring_types
                }))
                setOptionsTypeManagement(types.filter((type: any) => type.value != "1"))
            })

        const now = new Date();
        const firstDayOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

        setRangeDays({
            from: firstDayOfLastMonth,
            to: firstDayOfCurrentMonth
        })

        axios.get("/micro-areas").then(response => setMicroAreas(response.data))
    }, []);

    useEffect(() => {
        if (primaryFarmId != null) {
            axios.get(`/areas?farm_id=${primaryFarmId}`).then(response => setAreas(response.data))
        }
    }, [primaryFarmId]);

    useEffect(() => {
        if (primaryFarmId == null || !rangeDays || !areas) return;

        const selectedAreaIds = selectionToNumericIds(selectedAreas);
        let startDate = format(rangeDays?.from ? new Date(rangeDays?.from) : new Date(), 'yyyy-MM-dd')
        let endDate = format(rangeDays?.to ? new Date(rangeDays?.to) : new Date(), 'yyyy-MM-dd')
        const defaultAreas = areas.map(area => area.id)
        const areaIdsForQuery = selectedAreaIds.length > 0 ? selectedAreaIds : defaultAreas;

        axios.get(`/analytics/snapshots/management-groups?startDate=${startDate}&endDate=${endDate}&farmId=${primaryFarmId}&${areaIdsForQuery.map(area => `areaIds=${area}`).join("&")}`)
            .then(response => setManagementOptions(response.data.map((option: IAnalyticsManagementGroups) => ({
                label: `${option.management_type.name} - ${option.management_type.monitoring_types.map(el => el.name).join(",")}`,
                value: option.management_group_id,
                info: option
            }))))
    }, [primaryFarmId, rangeDays, areas, farmsKey, areasKey]);

    useEffect(() => {
        if (rangeDays && areas) {
            let startDate = format(rangeDays?.from ? new Date(rangeDays?.from) : new Date(), 'yyyy-MM-dd')
            let endDate = format(rangeDays?.to ? new Date(rangeDays?.to) : new Date(), 'yyyy-MM-dd')

            if (groupManagement) {
                const microAreas = groupManagement.map((management: IOptionProps) => (management.info as IAnalyticsManagementGroups).micro_areas.map(microArea => microArea.id)).flat()
                if (microAreas.length > 0)
                    axios.get(`/analytics/snapshots/micro-areas/metrics?startDate=${startDate}&endDate=${endDate}&${microAreas.length > 0 ? microAreas.map((microArea: number) => `microAreaIds=${microArea}`).join("&") : ""}`)
                        .then((response) => {
                            setSnapshotMicroAreaMetrics(response.data)
                        })
            }
        }
    }, [rangeDays, groupManagement]);

    const buildMicroAreaLabel = (microArea: any) => {
        const parts = [
            microArea?.farm_initials,
            microArea?.area_initials,
            microArea?.initials
        ].filter(Boolean);

        return parts.join("-");
    }

    useEffect(() => {
        if (snapshotMicroAreaMetrics) {
            setLotInstConsumo({
                data1: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.total_consumption_kg
                })),
                data2: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.average_instant_capacity_rate
                }))
            })
            setLotInstConsumoMicroArea(snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                label: inst.date,
                value: inst.total_consumption_kg,
                info: inst.micro_area.map((microArea) => ({
                    initials: buildMicroAreaLabel(microArea),
                    value: microArea.consumption_kg || 0
                })).sort((a, b) => a.initials.localeCompare(b.initials))
            })))
            setLotInstAltura({
                data1: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.average_height
                })),
                data2: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.average_instant_capacity_rate
                }))
            })
            setLotInstAlturaMicroArea(snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                label: inst.date,
                value: inst.average_height,
                info: inst.micro_area.map((microArea) => ({
                    initials: buildMicroAreaLabel(microArea),
                    value: microArea.height || 0
                })).sort((a, b) => a.initials.localeCompare(b.initials))
            })))

            setLotInstNota({
                data1: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.average_rating
                })),
                data2: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.average_instant_capacity_rate
                }))
            })
            setLotInstNotaMicroArea(snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                label: inst.date,
                value: inst.average_rating,
                info: inst.micro_area.map((microArea) => ({
                    initials: buildMicroAreaLabel(microArea),
                    value: microArea.rating || 0
                })).sort((a, b) => a.initials.localeCompare(b.initials))
            })))

            setLotInst(snapshotMicroAreaMetrics.daily_metrics.map((inst) => ({
                label: inst.date,
                value: inst.average_instant_capacity_rate,
                info: inst.micro_area.map((microArea) => ({
                    initials: buildMicroAreaLabel(microArea),
                    value: microArea.average_capacity_rate || 0
                })).sort((a, b) => a.initials.localeCompare(b.initials))
            })))

            setLotInstFaixa(({
                data1: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.strip_movements_count
                })),
                data2: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.average_instant_capacity_rate
                }))
            }))

            setLotInstFaixaMicroArea(snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                label: inst.date,
                value: inst.strip_movements_count,
                info: inst.micro_area.map((microArea) => ({
                    initials: buildMicroAreaLabel(microArea),
                    value: microArea.strip_movements || 0
                })).sort((a, b) => a.initials.localeCompare(b.initials))
            })))
        }
    }, [snapshotMicroAreaMetrics]);

    useEffect(() => {
        setValue("managementGroup", undefined);
        setLotInstConsumo(undefined);
        setLotInstConsumoMicroArea([]);
        setLotInstAltura(undefined);
        setLotInstAlturaMicroArea([]);
        setLotInstNota(undefined);
        setLotInstNotaMicroArea([]);
        setLotInstFaixa(undefined);
        setLotInstFaixaMicroArea([]);
        setLotInst([]);
        setSnapshotMicroAreaMetrics(undefined as any);
    }, [farmsKey]);

    const renderManagementGroupOption = (option: OptionV2) => {
        const infoOption: IAnalyticsManagementGroups = option.info;

        return (
            <div className="customDropdownOptions">
                {infoOption.management_type.name} - {infoOption.management_type.monitoring_types.map(el => el.name).join(",")}
                <br />
                {infoOption.micro_areas.length > 0 ? infoOption.micro_areas.map(microArea => microArea.initials).join(" ") : ""}
            </div>
        );
    }

    const flytoBoundsRef = useRef<FlyToBoundsRefProps>();

    useEffect(() => {
        if (groupManagement && groupManagement.length > 0) setMicroAreasSelected(groupManagement.map((management: IOptionProps) => (management.info as IAnalyticsManagementGroups).micro_areas.map(microArea => microArea.id)).flat())
    }, [groupManagement]);

    return (
        <div className="cattleMap_background" style={{ height: "100vh", overflowY: "auto" }}>
            <DateModal />

            <form onSubmit={() => handleSubmit(() => { })} className="dashboardPastureManagement">
                <div className="cattleMap_page">
                    <div className="cattleMap_header">
                        <BagFood height={72} />
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                        }}>
                            <h2 className="cattleMap_title">
                                Manejo de Pasto
                            </h2>
                            <h3 className="cattleMap_subtitle">
                                Visualização por Pasto
                            </h3>
                        </div>
                    </div>

                    <div>
                        <FilterDateDashboard
                            onExportExcel={() => {
                                if (snapshotMicroAreaMetrics) {
                                    try {
                                        exportPastureManagementMicroAreaDataToExcel(
                                            snapshotMicroAreaMetrics,
                                            rangeDays?.from ? new Date(rangeDays.from) : undefined,
                                            rangeDays?.to ? new Date(rangeDays.to) : undefined
                                        );
                                    } catch (error) {
                                        console.error('Erro ao exportar:', error);
                                        alert('Erro ao exportar dados. Tente novamente.');
                                    }
                                }
                            }}
                        />
                        <br />
                        <div style={{ width: "100%", maxWidth: 400 }}>
                            <Controller
                                name="managementType"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <MultiDropdownV2
                                        name="managementType"
                                        label="Tipo de pastejo"
                                        placeholder="Selecione..."
                                        menuMount="static"
                                        emptyMessage="Nenhuma opção disponível"
                                        options={optionsTypeManagement}
                                        value={field.value ?? []}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        error={error?.message}
                                    />
                                )}
                            />

                            <Controller
                                name="managementGroup"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <MultiDropdownV2
                                        name="managementGroup"
                                        label="Grupo de manejo"
                                        placeholder="Selecione..."
                                        menuMount="static"
                                        emptyMessage="Nenhuma opção disponível"
                                        options={typeManagement ? (managementOptions?.filter(option => Array.from(typeManagement.map(((type: IOptionProps) => type.value))).includes(`${option.info.management_type.id}`)) || []) : []}
                                        value={field.value ?? []}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        error={error?.message}
                                        renderOption={renderManagementGroupOption}
                                    />
                                )}
                            />

                            {primaryFarmCoords && primaryFarmCoords.length > 0 && <div className="mapFilterManagement">
                                <LeafletStaticMap>
                                    <FlyToBounds ref={flytoBoundsRef} fitCoords={primaryFarmCoords}>
                                        <Polygon
                                            dashArray={Constants.LEAFLET_DASHED_GREEN.dashArray}
                                            color={Constants.LEAFLET_DASHED_GREEN.color}
                                            fill={false}
                                            positions={primaryFarmCoords}
                                        />
                                        {/* microArea */}
                                        {microAreas?.filter(microarea => microAreasSelected?.includes(microarea.id))?.map(microarea => <Polygon
                                            positions={microarea.map_coords!}
                                            color={Constants.LEAFLET_UNFOCUS_TRANSLUCENT.color}
                                            fillColor={Constants.LEAFLET_NORMAL_BLUE.fillColor}
                                            fillOpacity={
                                                Constants.LEAFLET_UNFOCUS_TRANSLUCENT.fillOpacity
                                            }
                                        />)}

                                    </FlyToBounds>
                                </LeafletStaticMap>
                            </div>
                            }
                        </div>
                    </div>
                </div>

                <br />

                <div className="content">
                    {/* <ChartBarLine
                        titleGraph={"Taxa de lotação instantânea e kg de suplemento fornecido por dia"}
                        dataColumn={lotInstConsumo?.data1 || []}
                        dataLine={[]}
                        colorIcons={["Kg de suplemento fornecido", "Média de Tx lotação instantânea"]}
                        axisLabels={["", "Kg de suplemento", "Tx lotação instantânea"]}
                        fullWidth
                        info={lotInstConsumoMicroArea.map(inst => inst.info)}
                    /> */}
                    <ChartBar
                        titleGraph={"Kg de suplemento fornecido por dia"}
                        data={lotInstConsumo?.data1}
                        axisLabels={["", "Kg de suplemento fornecido"]}
                        fullWidth
                        info={lotInstConsumoMicroArea?.map(inst => inst.info)}
                        monoColor
                    />
                    {typeManagement && typeManagement.map(((type: IOptionProps) => type.value)).includes("2") ? <>
                        <div className="noBreakComponent">
                            <div className="infoContentGraph">
                                <h2>Número de dias no intervalo: {snapshotMicroAreaMetrics?.daily_metrics.length}</h2>
                                <h2>Número de coleta de altura: {snapshotMicroAreaMetrics?.total_monitoring_counts.height_count} coletas</h2>
                                <h2>Média de dias de ocupação: {Number(snapshotMicroAreaMetrics?.total_occupation_days.average_occupied_days).toFixed(1)}</h2>
                                <h2>Média de dias de descanso: {Number(snapshotMicroAreaMetrics?.total_occupation_days.average_rest_days).toFixed(1)}</h2>
                            </div>
                            {/* <ChartBarLine
                                titleGraph={"Taxa de lotação instatânea e altura de pasto em manejo contínuo"}
                                dataColumn={lotInstAltura?.data1 || []}
                                dataLine={lotInstAltura?.data2 || []}
                                colorIcons={["Média de Monitoramento", "Média de Tx lotação instantânea"]}
                                axisLabels={["", "Altura", "Tx lotação instantânea"]}
                                fullWidth
                                info={lotInstAlturaMicroArea.map(inst => inst.info)}
                            /> */}
                            <ChartBar
                                titleGraph={"Altura de pasto em manejo contínuo"}
                                data={lotInstAltura?.data1}
                                axisLabels={["", "Média de Altura"]}
                                fullWidth
                                info={lotInstAlturaMicroArea?.map(inst => inst.info)}
                                monoColor
                            />
                        </div>
                        <div className="noBreakComponent">
                            <div className="infoContentGraph">
                                <h2>Número de dias no intervalo: {snapshotMicroAreaMetrics?.daily_metrics.length}</h2>
                                <h2>Número de coleta de nota: {snapshotMicroAreaMetrics?.total_monitoring_counts.rating_count} coletas</h2>
                                <h2>Média de dias de ocupação: {Number(snapshotMicroAreaMetrics?.total_occupation_days.average_occupied_days).toFixed(1)}</h2>
                                <h2>Média de dias de descanso: {Number(snapshotMicroAreaMetrics?.total_occupation_days.average_rest_days).toFixed(1)}</h2>
                            </div>

                            {/* <ChartBarLine
                                titleGraph={"Taxa de lotação instatânea e nota de pasto em manejo contínuo"}
                                dataColumn={lotInstNota?.data1 || []}
                                dataLine={lotInstNota?.data2 || []}
                                colorIcons={["Média de Monitoramento", "Média de Tx lotação instantânea"]}
                                axisLabels={["", "Nota", "Tx lotação instantânea"]}
                                fullWidth
                                info={lotInstNotaMicroArea.map(inst => inst.info)}
                            /> */}
                            <ChartBar
                                titleGraph={"Nota de pasto em manejo contínuo"}
                                data={lotInstNota?.data1}
                                axisLabels={["", "Média de Nota"]}
                                fullWidth
                                info={lotInstNotaMicroArea?.map(inst => inst.info)}
                                monoColor
                            />
                        </div>
                    </> : <></>
                    }
                    {typeManagement && typeManagement.map(((type: IOptionProps) => type.value)).includes("3") ? <>
                        <div className="noBreakComponent">
                            <div className="infoContentGraph">
                                <h2>Média de dias de ocupação: {Number(snapshotMicroAreaMetrics?.total_occupation_days.average_occupied_days).toFixed(1)}</h2>
                                <h2>Média de dias de descanso: {Number(snapshotMicroAreaMetrics?.total_occupation_days.average_rest_days).toFixed(1)}</h2>
                            </div>
                            <ChartMultiLine
                                titleGraph={"Taxa de lotação instantânea em manejo rotacionado"}
                                data={lotInst}
                                axisLabels={["Dia", "Tx de lotação instantânea"]}
                                fullWidth
                            />
                        </div>
                        {snapshotMicroAreaMetrics?.total_monitoring_counts.height_count != 0 &&
                            <div className="noBreakComponent">
                                <div className="infoContentGraph">
                                    <h2>Número de dias no intervalo: {snapshotMicroAreaMetrics?.daily_metrics.length}</h2>
                                    <h2>Número de coleta de altura: {snapshotMicroAreaMetrics?.total_monitoring_counts.height_count} coletas</h2>
                                    <h2>Média de dias de ocupação: {Number(snapshotMicroAreaMetrics?.total_occupation_days.average_occupied_days).toFixed(1)}</h2>
                                    <h2>Média de dias de descanso: {Number(snapshotMicroAreaMetrics?.total_occupation_days.average_rest_days).toFixed(1)}</h2>
                                </div>
                                {/* <ChartBarLine
                                    titleGraph={"Taxa de lotação instatânea e altura de pasto em manejo rotacionado"}
                                    dataColumn={lotInstAltura?.data1 || []}
                                    dataLine={lotInstAltura?.data2 || []}
                                    colorIcons={["Média de Monitoramento", "Média de Tx lotação instantânea"]}
                                    axisLabels={["", "Altura", "Tx lotação instantânea"]}
                                    fullWidth
                                    info={lotInstAlturaMicroArea.map(inst => inst.info)}
                                /> */}
                                <ChartMultiLine
                                    titleGraph={"Altura de pasto em manejo rotacionado"}
                                    data={lotInstAlturaMicroArea}
                                    axisLabels={["Dia", "Altura (cm)"]}
                                    fullWidth
                                />
                            </div>
                        }
                        {snapshotMicroAreaMetrics?.total_monitoring_counts.rating_count != 0 &&
                            <div className="noBreakComponent">
                                <div className="infoContentGraph">
                                    <h2>Número de dias no intervalo: {snapshotMicroAreaMetrics?.daily_metrics.length}</h2>
                                    <h2>Número de coleta de nota: {snapshotMicroAreaMetrics?.total_monitoring_counts.rating_count} coletas</h2>
                                    <h2>Média de dias de ocupação: {Number(snapshotMicroAreaMetrics?.total_occupation_days.average_occupied_days).toFixed(1)}</h2>
                                    <h2>Média de dias de descanso: {Number(snapshotMicroAreaMetrics?.total_occupation_days.average_rest_days).toFixed(1)}</h2>
                                </div>
                                {/* <ChartBarLine
                                    titleGraph={"Taxa de lotação instatânea e nota de pasto em manejo rotacionado"}
                                    dataColumn={lotInstNota?.data1 || []}
                                    dataLine={[]}
                                    colorIcons={["Média de Monitoramento"]}
                                    axisLabels={["", "Nota"]}
                                    fullWidth
                                    info={lotInstNotaMicroArea.map(inst => inst.info)}
                                /> */}
                                <ChartBar
                                    titleGraph={"Nota de pasto em manejo rotacionado"}
                                    data={lotInstNota?.data1}
                                    axisLabels={["", "Média de Nota"]}
                                    fullWidth
                                    info={lotInstNotaMicroArea?.map(inst => inst.info)}
                                    monoColor
                                />
                            </div>
                        }
                    </> : <></>
                    }
                    {typeManagement && typeManagement.map(((type: IOptionProps) => type.value)).includes("4") ? <>
                        {/* <ChartBarLine
                            titleGraph={"Taxa de lotação instatânea e número de mudanças de faixa diárias"}
                            dataColumn={lotInstMovFaixa?.data1 || []}
                            dataLine={lotInstMovFaixa?.data2 || []}
                            colorIcons={["Número de mudanças de faixa por dia", "Tx lotação instantânea"]}
                            axisLabels={["", "N° de faixa", "Tx lotação instantânea"]}
                            fullWidth
                            info={lotInstMovFaixaMicroArea.map(inst => inst.info)}
                        /> */}
                        <ChartBar
                            titleGraph={"Número de mudanças de faixa diárias"}
                            data={lotInstMovFaixa?.data1}
                            axisLabels={["", "N° de faixa"]}
                            fullWidth
                            info={lotInstMovFaixaMicroArea?.map(inst => inst.info)}
                            monoColor
                        />
                    </> : <></>
                    }
                </div>
            </form >
        </div >
    );
}
